import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { useAppConfig } from '../contexts/AppConfigContext'

/**
 * Background options for PageLayout:
 * - 'default': Uses CSS variable --color-page-bg (solid color)
 * - 'gradient': Uses CSS variables --color-page-bg-gradient-from/to
 * - 'white': Simple white background
 * - 'transparent': No background (inherits from parent)
 * - { color: string }: Custom solid color
 * - { gradient: string }: Custom gradient (full CSS gradient value)
 * - { image: string, overlay?: string }: Background image with optional overlay
 */
export type PageBackground =
  | 'default'
  | 'gradient'
  | 'white'
  | 'transparent'
  | { color: string }
  | { gradient: string }
  | { image: string; overlay?: string }

interface PageLayoutProps {
  children: ReactNode
  withLogin?: boolean
  /** Background style - defaults to 'gradient' for backward compatibility */
  background?: PageBackground
  /** Additional CSS classes for the outer container */
  className?: string
  /** Expand content to full width (default: max-w-4xl) */
  fullWidth?: boolean
}

export default function PageLayout({
  children,
  withLogin = false,
  background = 'gradient',
  className,
  fullWidth = false,
}: PageLayoutProps) {
  const { t } = useTranslation()
  const { logo } = useAppConfig()

  const getBackgroundStyles = (): {
    className: string
    style?: React.CSSProperties
  } => {
    if (typeof background === 'string') {
      switch (background) {
        case 'default':
          return { className: 'page-bg' }
        case 'gradient':
          return { className: 'page-bg-gradient' }
        case 'white':
          return { className: 'bg-white' }
        case 'transparent':
          return { className: 'bg-transparent' }
      }
    }

    if ('color' in background) {
      return { className: '', style: { backgroundColor: background.color } }
    }

    if ('gradient' in background) {
      return { className: '', style: { background: background.gradient } }
    }

    if ('image' in background) {
      const overlay = background.overlay || 'transparent'
      return {
        className: '',
        style: {
          backgroundImage: `linear-gradient(${overlay}, ${overlay}), url(${background.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        },
      }
    }

    return { className: '' }
  }

  const bgStyles = getBackgroundStyles()

  return (
    <div
      className={twMerge('min-h-dvh', bgStyles.className, className)}
      style={bgStyles.style}
    >
      <main
        className={twMerge(
          'mx-auto flex min-h-dvh flex-col gap-14 px-8 py-14',
          fullWidth ? 'max-w-full' : 'max-w-4xl',
        )}
      >
        {withLogin && !('ReactNativeWebView' in window) && (
          <header
            className={twMerge(
              'flex shrink-0 items-center justify-between',
              fullWidth && 'mx-auto w-full max-w-5xl',
            )}
          >
            {logo}

            <Link
              className="custom-heading hover:opacity-80"
              to="/account/sign-in"
              data-testid="button-login"
            >
              {t('shared.signIn')}
            </Link>
          </header>
        )}

        {children}
      </main>
    </div>
  )
}
